import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { GraduationCap, Building2, Loader2, ArrowRight } from "lucide-react";

export default function RoleSelection() {
  const { t } = useTranslation();
  const { selectRole } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);

  const choose = async (role) => {
    setBusy(role);
    try {
      const u = await selectRole(role);
      navigate(u?.role === "employer" ? "/employer" : "/dashboard", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not select role");
      setBusy(null);
    }
  };

  const cards = [
    {
      role: "student", icon: GraduationCap, testid: "role-student",
      title: t("role.studentTitle"), desc: t("role.studentDesc"),
      accent: "from-amber-500 to-orange-600",
    },
    {
      role: "employer", icon: Building2, testid: "role-employer",
      title: t("role.employerTitle"), desc: t("role.employerDesc"),
      accent: "from-indigo-600 to-indigo-900",
    },
  ];

  return (
    <div className="min-h-screen bg-[#FFFDF9] flex items-center justify-center px-4 py-16" data-testid="role-selection">
      <div className="w-full max-w-4xl">
        <div className="text-center mb-10">
          <img src="/logo.png" alt="" className="mx-auto h-14 w-14 object-contain" />
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
            {t("role.heading")}
          </h1>
          <p className="mt-4 text-lg text-slate-500">{t("role.subheading")}</p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          {cards.map((c) => (
            <button
              key={c.role}
              onClick={() => choose(c.role)}
              disabled={!!busy}
              data-testid={c.testid}
              className="group text-left rounded-3xl border-2 border-slate-200 bg-white p-8 transition-all hover:-translate-y-1 hover:border-amber-400 hover:shadow-2xl disabled:opacity-60"
            >
              <div className={`h-16 w-16 rounded-2xl bg-gradient-to-br ${c.accent} flex items-center justify-center shadow-lg`}>
                <c.icon className="h-8 w-8 text-white" />
              </div>
              <h2 className="mt-6 font-heading text-2xl font-bold text-slate-900">{c.title}</h2>
              <p className="mt-2 text-base leading-relaxed text-slate-500">{c.desc}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-base font-bold text-amber-600 group-hover:gap-3 transition-all">
                {busy === c.role ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{t("role.continue")} <ArrowRight className="h-4 w-4" /></>}
              </span>
            </button>
          ))}
        </div>
        <p className="mt-8 text-center text-sm text-slate-400">{t("role.note")}</p>
      </div>
    </div>
  );
}
